import React, { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

const ContentMetadata = ({ content }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!content) return null

  const formatGenres = (genres) => {
    if (!genres || genres.length === 0) return 'N/A'
    return genres.map(genre => genre.name).join(', ')
  }

  const getCastByType = (castAndCrew, roleType) => {
    if (!castAndCrew || castAndCrew.length === 0) return []
    return castAndCrew.filter(person => person.role_type === roleType)
  }

  const actors = getCastByType(content.cast_and_crew, 'actor')
  const directors = getCastByType(content.cast_and_crew, 'director')
  const writers = getCastByType(content.cast_and_crew, 'writer')
  const producers = getCastByType(content.cast_and_crew, 'producer')

  const renderPersonList = (people, label) => {
    if (!people || people.length === 0) return null
    
    return (
      <div className="space-y-1">
        <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
        <dd className="text-sm">
          {people.map((person, index) => (
            <span key={person.id}>
              {person.name}
              {person.role && person.role !== person.role_type && (
                <span className="text-muted-foreground"> as {person.role}</span>
              )}
              {index < people.length - 1 && ', '}
            </span>
          ))}
        </dd>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Description */}
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">About</h3>
        <div className={`text-muted-foreground ${isExpanded ? '' : 'line-clamp-3'}`}>
          {content.description || 'No description available.'}
        </div>
        {content.description && content.description.length > 200 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center space-x-1 text-primary text-sm hover:underline"
          >
            <span>{isExpanded ? 'Show less' : 'Show more'}</span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        )}
      </div>

      {/* Genres */}
      {content.genres && content.genres.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {content.genres.map((genre) => (
            <span
              key={genre.id}
              className="px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-xs font-medium"
            >
              {genre.name}
            </span>
          ))}
        </div>
      )}

      {/* Metadata Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-card rounded-lg">
        <dl className="space-y-3">
          {/* Content Type */}
          <div className="space-y-1">
            <dt className="text-sm font-medium text-muted-foreground">Type</dt>
            <dd className="text-sm capitalize">
              {content.content_type?.replace('_', ' ') || 'Unknown'}
            </dd>
          </div>

          {/* Release Date */}
          {content.release_date && (
            <div className="space-y-1">
              <dt className="text-sm font-medium text-muted-foreground">Release Date</dt>
              <dd className="text-sm">
                {new Date(content.release_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </dd>
            </div>
          )}

          {/* Duration */}
          {content.duration_seconds && (
            <div className="space-y-1">
              <dt className="text-sm font-medium text-muted-foreground">Duration</dt>
              <dd className="text-sm">
                {Math.floor(content.duration_seconds / 3600) > 0 && (
                  `${Math.floor(content.duration_seconds / 3600)}h `
                )}
                {Math.floor((content.duration_seconds % 3600) / 60)}m
              </dd>
            </div>
          )}

          {/* Rating */}
          {content.rating && (
            <div className="space-y-1">
              <dt className="text-sm font-medium text-muted-foreground">Rating</dt>
              <dd className="text-sm">
                <span className="px-1 border border-muted-foreground rounded text-xs">
                  {content.rating}
                </span>
              </dd>
            </div>
          )}
        </dl>

        <dl className="space-y-3">
          {/* IMDb Rating */}
          {content.imdb_rating && (
            <div className="space-y-1">
              <dt className="text-sm font-medium text-muted-foreground">IMDb Rating</dt>
              <dd className="text-sm flex items-center space-x-1">
                <span className="text-yellow-500">⭐</span>
                <span>{content.imdb_rating}/10</span>
              </dd>
            </div>
          )}

          {/* View Count */}
          {content.view_count !== undefined && (
            <div className="space-y-1">
              <dt className="text-sm font-medium text-muted-foreground">Views</dt>
              <dd className="text-sm">
                {content.view_count >= 1000000 
                  ? `${(content.view_count / 1000000).toFixed(1)}M`
                  : content.view_count >= 1000
                  ? `${(content.view_count / 1000).toFixed(1)}K`
                  : content.view_count.toString()
                }
              </dd>
            </div>
          )}

          {/* Language */}
          {content.language && (
            <div className="space-y-1">
              <dt className="text-sm font-medium text-muted-foreground">Language</dt>
              <dd className="text-sm capitalize">{content.language}</dd>
            </div>
          )}

          {/* Status */}
          {content.status && (
            <div className="space-y-1">
              <dt className="text-sm font-medium text-muted-foreground">Status</dt>
              <dd className="text-sm capitalize">{content.status.replace('_', ' ')}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Cast and Crew */}
      {content.cast_and_crew && content.cast_and_crew.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Cast & Crew</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <dl className="space-y-3">
              {renderPersonList(directors, 'Director(s)')}
              {renderPersonList(writers, 'Writer(s)')}
              {renderPersonList(producers, 'Producer(s)')}
            </dl>
            <dl className="space-y-3">
              {renderPersonList(actors.slice(0, 10), 'Cast')}
              {actors.length > 10 && (
                <dd className="text-sm text-muted-foreground">
                  and {actors.length - 10} more...
                </dd>
              )}
            </dl>
          </div>
        </div>
      )}

      {/* Additional Info */}
      {(content.content_warning || content.accessibility_features) && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Additional Information</h3>
          
          {content.content_warning && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <h4 className="text-sm font-medium text-yellow-700 dark:text-yellow-400 mb-1">
                Content Warning
              </h4>
              <p className="text-xs text-yellow-600 dark:text-yellow-300">
                {content.content_warning}
              </p>
            </div>
          )}

          {content.accessibility_features && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <h4 className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-1">
                Accessibility Features
              </h4>
              <p className="text-xs text-blue-600 dark:text-blue-300">
                {Array.isArray(content.accessibility_features) 
                  ? content.accessibility_features.join(', ')
                  : content.accessibility_features
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ContentMetadata